import { useQuery } from "@tanstack/react-query";

import {
  ICustomFieldColumn,
  ICustomFieldFormItem,
  IPatientCustomFieldValue,
} from "../../@types/CustomField";
import { customFieldsService } from "../../services/customFieldsService";

// The api instance's response interceptor resolves errors to `error.response` instead of
// rejecting, so a non-2xx call yields an ErrorResponse body rather than an array. Guard against
// that (e.g. 404 "no access") by falling back to an empty list.
function asArray<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

// Module-level empty lists so the "not loaded yet" fallback keeps a STABLE identity across
// renders. A `?? []` literal here would hand out a brand-new array on every render, and any
// consumer with that list in a useEffect dep array would re-run the effect on every render —
// setting state from it then loops forever (React only bails out on Object.is equality). That
// is exactly what broke the Add Patient dropdowns while no ward was selected: with the query
// disabled, `data` stays undefined, so the fallback was recreated on each render.
const EMPTY_FORM_ITEMS: ICustomFieldFormItem[] = [];
const EMPTY_PATIENT_VALUES: IPatientCustomFieldValue[] = [];
const EMPTY_COLUMNS: ICustomFieldColumn[] = [];

// EN-5067: the custom fields to render on Add Patient for the currently selected ward set.
export function useCustomFieldsForWards(wardIds: number[]) {
  // Sorted so the key is stable regardless of ward selection order.
  const sortedWardIds = [...wardIds].sort((a, b) => a - b);

  const { data, isError, isFetching, isLoading } = useQuery<
    ICustomFieldFormItem[]
  >({
    queryKey: ["custom-fields-for-wards", sortedWardIds],
    queryFn: async () => {
      const { data } = await customFieldsService.getCustomFieldsForWards(
        sortedWardIds
      );

      return asArray<ICustomFieldFormItem>(data);
    },
    enabled: sortedWardIds.length > 0,
  });

  return {
    customFields: (data as ICustomFieldFormItem[]) ?? EMPTY_FORM_ITEMS,
    isCustomFieldsFetching: isFetching,
    isCustomFieldsError: isError,
    isCustomFieldsLoading: isLoading,
  };
}

// EN-5067: a patient's saved custom-field values for the side panel (read-only).
export function usePatientCustomFields(patientId?: number) {
  const { data, isError, isFetching, isFetched, isSuccess, refetch } = useQuery<
    IPatientCustomFieldValue[]
  >({
    queryKey: ["patient-custom-fields", patientId],
    queryFn: async () => {
      // `enabled` guarantees patientId is defined before this runs.
      const { data } = await customFieldsService.getPatientCustomFields(
        patientId as number
      );

      return asArray<IPatientCustomFieldValue>(data);
    },
    enabled: !!patientId,
  });

  return {
    patientCustomFields:
      (data as IPatientCustomFieldValue[]) ?? EMPTY_PATIENT_VALUES,
    isPatientCustomFieldsFetching: isFetching,
    isPatientCustomFieldsError: isError,
    isPatientCustomFieldsFetched: isFetched,
    isPatientCustomFieldsSuccess: isSuccess,
    refetchPatientCustomFields: refetch,
  };
}

// EN-5067: the custom fields available as optional patient-list columns for this staff member.
export function usePatientListColumns() {
  const { data, isError, isFetching } = useQuery<ICustomFieldColumn[]>({
    queryKey: ["patient-list-custom-columns"],
    queryFn: async () => {
      const { data } = await customFieldsService.getPatientListColumns();

      return asArray<ICustomFieldColumn>(data);
    },
  });

  return {
    customColumns: (data as ICustomFieldColumn[]) ?? EMPTY_COLUMNS,
    isCustomColumnsFetching: isFetching,
    isCustomColumnsError: isError,
  };
}
